import React, { Component } from "react";
import { Link } from "react-router-dom";
import "animate.css";

import Navbar from "../../Navbar/Navbar";
import MainBodyContainer from "../../Common/Body/MainBodyContainer/MainBodyContainer";
import Footer from "../../Footer/Footer";

// We will use the generated images once available. For now, we'll assume they will be moved to the images folder or loaded from brain. 
// Assuming the user will move them or we will move them, but for now we will use placeholder paths that we will replace if needed, 
// or I can just use the absolute path to the brain images if I can.
// I will import the images directly from the artifact directory later, but for now let's use standard imports.
import heroImage from "../../../images/people/training/workshop-collaboration.jpg";
import aboutImage from "../../../images/people/training/workshop-collaboration.jpg";
import "./CommunityOutreach.css";

class CommunityOutreach extends Component {
  constructor(props) {
    super(props);
    this.state = {
      visibleSections: new Set(),
      activeCohortIndex: 0,
    };
    this.observerRef = null;
    this.sectionRefs = {};
  }

  componentDidMount() {
    this.setupSectionObserver();
  }

  componentWillUnmount() {
    if (this.observerRef) this.observerRef.disconnect();
  }

  setupSectionObserver = () => {
    this.observerRef = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.setState((prev) => ({
              visibleSections: new Set([...prev.visibleSections, entry.target.dataset.section]),
            }));
          }
        });
      },
      { threshold: 0.15 }
    );
    Object.values(this.sectionRefs).forEach((ref) => {
      if (ref) this.observerRef.observe(ref);
    });
  };

  registerSection = (id) => (el) => {
    if (el && !this.sectionRefs[id]) {
      this.sectionRefs[id] = el;
      el.dataset.section = id;
      if (this.observerRef) this.observerRef.observe(el);
    }
  };

  isVisible = (id) => this.state.visibleSections.has(id);

  // ─── Content ────

  cohorts = [
    { label: "Women & Mothers", icon: "👩‍👧", color: "#e88a7a" },
    { label: "Ethnic Minorities", icon: "🌍", color: "#d9a05b" },
    { label: "Young Adults (19-24)", icon: "🎓", color: "#5b8c85" },
    { label: "People Seeking Asylum", icon: "🕊️", color: "#6a7b9c" },
    { label: "Low Skills & Negative Past Experiences", icon: "🌱", color: "#8a5c7a" },
    { label: "Impacted by Cost-of-Living", icon: "💷", color: "#d9735b" },
    { label: "Learning Difficulties / Disabled", icon: "🧩", color: "#b36b8a" },
    { label: "Digitally Excluded", icon: "💻", color: "#4b6a82" },
  ];

  services = [
    {
      num: "01",
      title: "Outreach Activities",
      body: "Tailored community engagement to promote adult skills and opportunities where people already gather.",
      icon: "📢"
    },
    {
      num: "02",
      title: "Workshops & Events",
      body: "Safe, collaborative spaces to begin the learning journey, challenging negative attitudes towards education.",
      icon: "🤝"
    },
    {
      num: "03",
      title: "1-on-1 Support Sessions",
      body: "Individualized assessments to identify specific needs and create tailored progression action plans.",
      icon: "👤"
    },
    {
      num: "04",
      title: "Referral Pathways",
      body: "Building strong, lasting relationships with local skills providers for seamless integration and continued learning.",
      icon: "🔗"
    }
  ];

  render() {
    return (
      <div className="co-page">
        <Navbar />
        <div>
          <MainBodyContainer heading={undefined}>

            {/* ───── HERO ───── */}
            <div className="co-hero">
              <div className="co-hero-bg">
                 <img src={heroImage} alt="Community learners" className="co-hero-img" onError={(e) => e.target.style.display = 'none'} />
                 <div className="co-hero-overlay"></div>
              </div>

              <div className="co-hero-inner">
                <div className="co-hero-kicker animate__animated animate__fadeInDown">
                  <span className="co-kicker-line"></span>
                  <span>Supported by the Mayor of London</span>
                  <span className="co-kicker-line"></span>
                </div>

                <h1 className="co-hero-headline animate__animated animate__fadeInUp">
                  Breaking Down Barriers to <br />
                  <em>Adult Education.</em>
                </h1>

                <p className="co-hero-body animate__animated animate__fadeInUp animate__delay-1s">
                  The Skills for Londoners Community Outreach Programme 2024-25 delivered by Alisar Prospects Training. 
                  We are proud to support Londoners in making their first step back into learning in a safe, welcoming, and tailored environment.
                </p>

                <div className="co-hero-chips animate__animated animate__fadeInUp animate__delay-1s">
                  <span className="co-chip">✦ GLA Funded Project</span>
                  <span className="co-chip">✦ 500+ Lives Impacted</span>
                </div>

                <div className="co-hero-actions animate__animated animate__fadeInUp animate__delay-1s">
                  <Link to="/contact?inquiry=community-outreach" className="co-btn-primary">
                    Get Support
                    <span className="co-btn-arrow">→</span>
                  </Link>
                  <Link to="/contact?inquiry=outreach-partner" className="co-btn-ghost">
                    Partner with Us
                  </Link>
                </div>
              </div>
            </div>

            {/* ───── IMPACT STRIP ───── */}
            <div
              className={`co-impact ${this.isVisible("impact") ? "revealed" : ""}`}
              ref={this.registerSection("impact")}
            >
              <div className="co-impact-inner">
                <div className="co-stat">
                  <div className="co-stat-val">200+</div>
                  <div className="co-stat-label">People & families</div>
                  <div className="co-stat-sub">Across 6+ London Boroughs</div>
                </div>
                <div className="co-stat-divider"></div>
                <div className="co-stat">
                  <div className="co-stat-val">70%</div>
                  <div className="co-stat-label">Supported into Numeracy & Life Skills</div>
                  <div className="co-stat-sub">Exceeded 60% KPI</div>
                </div>
                <div className="co-stat-divider"></div>
                <div className="co-stat">
                  <div className="co-stat-val">50%+</div>
                  <div className="co-stat-label">Into English / Vocational Training</div>
                  <div className="co-stat-sub">Exceeded 40% KPI</div>
                </div>
                <div className="co-stat-divider"></div>
                <div className="co-stat highlighted">
                  <div className="co-stat-icon">🌟</div>
                  <div className="co-stat-label">Holistic Progression</div>
                  <div className="co-stat-desc">Advocacy, careers advice, employment, and vocational training.</div>
                </div>
              </div>
            </div>

            {/* ───── MISSION (ABOUT) ───── */}
            <section
              className={`co-about ${this.isVisible("about") ? "revealed" : ""}`}
              ref={this.registerSection("about")}
            >
              <div className="co-about-grid">
                <div className="co-about-visual">
                  <div className="co-image-frame">
                    <img src={aboutImage} alt="1-on-1 Mentoring session" onError={(e) => e.target.style.display = 'none'} />
                    <div className="co-image-caption">
                      <span className="co-caption-mark">✦</span>
                      Challenging negative attitudes to learning
                    </div>
                  </div>
                </div>
                <div className="co-about-text">
                  <span className="co-eyebrow">The Project Mission</span>
                  <h2 className="co-section-heading">Tailored Support for Diverse Communities.</h2>
                  <p>
                    This project was designed to address educational barriers and challenge negative attitudes towards learning, 
                    especially in numeracy and literacy. 
                  </p>
                  <p>
                    We focus on ethnic minority groups, mothers, young people, and people seeking asylum in London, helping them 
                    navigate their options through bespoke action plans and compassionate, 1-on-1 support.
                  </p>
                  <div className="co-boroughs">
                    <div className="co-boroughs-title">Proudly Serving:</div>
                    <div className="co-borough-list">
                      Newham • Enfield • Redbridge • Barking & Dagenham • Havering
                    </div>
                  </div>
                  <Link to="/contact?inquiry=community-outreach" className="co-about-cta">
                    Contact our outreach team <span>→</span>
                  </Link>
                </div>
              </div>
            </section>

            {/* ───── COHORT ───── */}
            <section
              className={`co-cohort ${this.isVisible("cohort") ? "revealed" : ""}`}
              ref={this.registerSection("cohort")}
            >
              <div className="co-cohort-header">
                <span className="co-eyebrow">Who We Support</span>
                <h2 className="co-section-heading centered">
                  Empowering London's Most <em>Diverse</em> Voices.
                </h2>
                <p className="co-section-sub">
                  We specialize in reaching individuals who are often left behind by traditional adult education systems.
                </p>
              </div>

              <div className="co-cohort-grid">
                {this.cohorts.map((cohort, i) => (
                  <div 
                    key={i} 
                    className="co-cohort-card"
                    style={{ "--card-accent": cohort.color, animationDelay: `${i * 0.1}s` }}
                  >
                    <div className="co-cohort-icon">{cohort.icon}</div>
                    <div className="co-cohort-label">{cohort.label}</div>
                  </div>
                ))}
              </div>
            </section>

            {/* ───── HOW WE DO IT ───── */}
            <section
              className={`co-services ${this.isVisible("services") ? "revealed" : ""}`}
              ref={this.registerSection("services")}
            >
              <div className="co-services-inner">
                <div className="co-services-header">
                  <span className="co-eyebrow">Core Services</span>
                  <h2 className="co-section-heading">How We Create Pathways.</h2>
                  <p>
                    Our approach is highly individualized, ensuring every participant receives the exact 
                    support they need to take the next step in their educational journey.
                  </p>
                </div>

                <div className="co-services-list">
                  {this.services.map((service, i) => (
                    <div key={i} className="co-service-item">
                      <div className="co-service-icon-wrap">
                        <span className="co-service-icon">{service.icon}</span>
                        <div className="co-service-num">{service.num}</div>
                      </div>
                      <div className="co-service-content">
                        <h4 className="co-service-title">{service.title}</h4>
                        <p className="co-service-body">{service.body}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* ───── PARTNERSHIPS ───── */}
            <section
              className={`co-partners ${this.isVisible("partners") ? "revealed" : ""}`}
              ref={this.registerSection("partners")}
            >
              <div className="co-partner-box">
                <span className="co-partner-icon">🤝</span>
                <h2 className="co-partner-heading">Stronger Together</h2>
                <p className="co-partner-body">
                  We proudly collaborate with the <strong>Local London</strong> and <strong>South London Partnership</strong> integration hubs. 
                  This ensures a joined-up approach to community support, creating robust referral pathways and 
                  maximizing the impact of the Skills for Londoners Programme.
                </p>
              </div>
            </section>

            {/* ───── FINAL CTA ───── */}
            <section
              className={`co-final ${this.isVisible("final") ? "revealed" : ""}`}
              ref={this.registerSection("final")}
            >
              <div className="co-final-bg" aria-hidden="true"></div>
              <div className="co-final-content">
                <span className="co-final-mark">✦</span>
                <h2 className="co-final-heading">
                  Ready to take the <br /><em>next step?</em>
                </h2>
                <p className="co-final-body">
                  Reach out to our dedicated provider contact to learn more about our 
                  outreach services, make a referral, or partner with us.
                </p>
                

                <div className="co-final-actions">
                  <Link to="/contact" className="co-btn-primary large">
                    Contact Us
                    <span className="co-btn-arrow">→</span>
                  </Link>
                </div>
              </div>
            </section>

          </MainBodyContainer>
        </div>
        <Footer />
      </div>
    );
  }
}

export default CommunityOutreach;
